import { useMemo, useState } from 'react'
import { App, Button, Tag, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { shortId } from '@/shared/lib/shortId'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { GeoFilterBar } from '@/features/geo/GeoFilterBar'
import { GeoFormModal } from '@/features/geo/GeoFormModal'
import { geoColumns } from '@/features/geo/geoColumns'
import { useRegionOptions } from '@/features/geo/useGeoOptions'
import {
  ACTIVE_FILTER,
  REGION_FILTER,
  SHOW_ALL,
  deactivateDistrict,
  fetchDistrictPage,
  updateDistrict,
  type District,
} from '@/features/geo/geoApi'

export function DistrictsPage() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()
  const { state, patch, reset } = useTableUrlState()
  const [editing, setEditing] = useState<District | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  const regions = useRegionOptions(false)
  const regionNames = useMemo(
    () => new Map(regions.options.map((r) => [r.id, r.name])),
    [regions.options],
  )

  const selectedRegionId = state.filters[REGION_FILTER]?.[0] ?? null
  const isFiltered = state.search !== '' || Object.keys(state.filters).length > 0

  /**
   * ⚠️ Sabab `RegionsPage` dagi bilan bir xil: nofaol tumanlar sukut
   * bo'yicha yashirin, ya'ni bo'sh ro'yxat «tuman yo'q» degani EMAS.
   *
   * ⚠️ VILOYAT FILTRI SAQLANADI — u boshqa savol («shu viloyatning
   * tumanlari»). Uni birga o'chirish operatorni tanlagan viloyatidan
   * jimgina chiqarib yuborardi.
   */
  const showAll = () => patch({ page: 1, filters: { ...state.filters, [ACTIVE_FILTER]: [SHOW_ALL] } })

  const regionFilters = useMemo(() => {
    const items = regions.options.map((r) => ({ text: r.name, value: r.id }))
    if (selectedRegionId !== null && !regionNames.has(selectedRegionId)) {
      items.unshift({ text: shortId(selectedRegionId), value: selectedRegionId })
    }
    return items
  }, [regions.options, regionNames, selectedRegionId])

  const toggle = useMutation({
    mutationFn: async (row: District) => {
      if (row.active) return deactivateDistrict(row.id)
      await updateDistrict(row.id, {
        regionId: row.regionId,
        name: row.name,
        code: row.code,
        active: true,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.geo.all })
      void message.success(t('common.toastSaved'))
    },
    onError: (error) => void message.error(translateError(error, t)),
  })

  const openForm = (row: District | null) => {
    setEditing(row)
    setFormOpen(true)
  }

  const toggleActive = (row: District) => {
    if (!row.active) {
      toggle.mutate(row)
      return
    }
    modal.confirm({
      title: t('geo.districts.deactivateTitle'),
      content: t('geo.districts.deactivateConfirm', { name: row.name }),
      okText: t('geo.deactivate'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: () => toggle.mutate(row),
    })
  }

  const columns = useMemo(
    () =>
      geoColumns<District>({
        t,
        extra: [
          {
            /*
              ⚠️ Ustun kaliti FILTR kaliti bilan bir xil va `filters`
              MAJBURIY. `DataTable.handleChange` filtrlarni antd bergan
              ro'yxatdan QAYTA quradi, antd esa faqat `filters` e'lon
              qilgan ustunlarni biladi — busiz sahifa almashtirilishi
              bilan viloyat filtri jimgina tushib qolardi.

              ⚠️ Tanlangan id ro'yxatga MAJBURAN qo'shiladi: antd qiymatni
              `filters` bo'yicha filtrlaydi va reyestr hali yuklanmagan
              paytda (yoki chegaradan oshganda) tanlov yo'qolardi.
            */
            key: REGION_FILTER,
            title: t('geo.region'),
            width: 200,
            filters: regionFilters,
            filterMultiple: false,
            filterSearch: true,
            // Server nom bermasa xarita ishlatiladi; u ham bilmasa — qisqa id.
            // Butun ustunni bo'sh qoldirish "viloyatsiz tuman" degan yolg'on
            // xulosaga olib borardi.
            render: (_, row) => {
              const name = row.regionName ?? regionNames.get(row.regionId)
              return name === undefined ? (
                <Typography.Text type="secondary" code>
                  {shortId(row.regionId)}
                </Typography.Text>
              ) : (
                <Tag>{name}</Tag>
              )
            },
          },
        ],
        onEdit: openForm,
        onToggleActive: toggleActive,
        pendingId: toggle.isPending ? (toggle.variables?.id ?? null) : null,
      }),
    // ⚠️ `openForm` va `toggleActive` ATAYLAB yo'q — sabab `RegionsPage`
    //    dagi bilan bir xil: har chizishda qayta yaratiladi, lekin faqat
    //    holat o'rnatuvchilarni va barqaror `toggle.mutate` ni chaqiradi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, regionNames, regionFilters, toggle.isPending, toggle.variables],
  )

  return (
    <>
      <PageHeader
        title={t('nav.geoDistricts')}
        description={t('pageIntro.geoDistricts')}
        extra={
          <Can permission="geo.write">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
              {t('geo.districts.createTitle')}
            </Button>
          </Can>
        }
      />

      <DataTable<District>
        queryKey={queryKeys.geo.districts}
        fetchPage={fetchDistrictPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="geo-districts"
        stickyFirstColumn
        // Javobda `total` bo'lmasligi mumkin (`geoApi` sarlavhasi) — yasalgan
        // son aniq ko'rsatilsa YOLG'ON bo'lardi
        showTotal
        searchable
        searchPlaceholder={t('geo.searchPlaceholder')}
        toolbarExtra={<GeoFilterBar withRegion />}
        scrollX={1060}
        {...(isFiltered
          ? {
              emptyTitle: t('dataTable.emptyFiltered'),
              emptyDescription: t('dataTable.emptyFilteredHint'),
              emptyAction: { label: t('dataTable.resetFilters'), onClick: reset },
            }
          : {
              emptyTitle: t('geo.districts.empty'),
              emptyDescription: t('geo.districts.emptyHint'),
              emptyAction: { label: t('dataTable.stateAll'), onClick: showAll },
            })}
      />

      <GeoFormModal
        open={formOpen}
        kind="district"
        entry={editing}
        defaultRegionId={selectedRegionId}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
      />
    </>
  )
}
